import { useState } from 'react';
import {
  Flex,
  Box,
  Icon,
  Textarea,
  Button,
  Avatar,
  IconButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Time } from '@/ui/Time';

import { updateCandidateNotes } from '@/store/circleCandidates/actions';
import { File02 as Paper } from '@kula-ai/icons/solid/File02';
import { Edit05 as Edit } from '@kula-ai/icons/outline/Edit05';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { PrivateNote } from '@/assets/icons/bold';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';

const noteSchema = Yup.object().shape({
  body: Yup.string().required('Note body is Required'),
  private: Yup.bool(),
});

const NoteView = ({ note, onEdit, size = 'lg' }) => {
  return (
    <Flex position="relative" role="group" className="notes">
      <Flex flexDir="column" alignItems="center">
        {size !== 'sm' && (
          <Flex
            width="36px"
            height="36px"
            background="#fbb13c4d"
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
          >
            <Icon
              as={note?.private ? PrivateNote : Paper}
              width="22px"
              height="22px"
              color="#FF9C00"
            />
          </Flex>
        )}
      </Flex>
      <Box
        flex="1"
        background="secondary-25"
        borderRadius="14px"
        wordBreak="break-all"
        mb="30px"
        p="20px"
        ml={size !== 'sm' ? '20px' : 0}
      >
        <KText whiteSpace="pre-wrap">{note?.body}</KText>
        <KText variant="muted" color="secondary-500" mt="sm">
          <Avatar
            className="avatar-sm"
            name={note?.user?.name}
            width="20px"
            height="20px"
            borderRadius="4px"
            src={note?.user?.image_url}
          />
          <KText
            as="span"
            variant="muted"
            color="secondary-500"
            pl="4px"
            textTransform="capitalize"
          >
            Created by {note.user.name} |
          </KText>
          <KText as="span" variant="muted" color="secondary-500" pl="4px">
            Last Modified at
          </KText>
          <Time
            as="span"
            time={note.updated_at}
            variant="muted"
            color="secondary-500"
            ml="5px"
          />
        </KText>
        <Box
          display="none"
          _groupHover={{ display: 'block' }}
          position="absolute"
          top="5px"
          right="10px"
        >
          <IconButton
            aria-label="Edit notes"
            width="32px"
            height="32px"
            background="white"
            borderWidth="1px"
            borderColor="border-color"
            borderRadius="6px"
            onClick={onEdit}
            _hover={{ bg: 'white', boxShadow: 'border-color' }}
            _focus={{ bg: 'white' }}
            _active={{ bg: 'white' }}
            icon={
              <Icon as={Edit} width="16px" height="16px" color="light.300" />
            }
          />
        </Box>
      </Box>
    </Flex>
  );
};

export const NoteForm = ({
  note,
  edit,
  onCancel = () => {},
  onSave = () => {},
  size = 'lg',
}) => {
  const currContact = useSelector(selectCurrUser);
  const onSubmit = async (payload, { resetForm }) => {
    await onSave(payload);
    resetForm();
  };

  return (
    <Box mb="xl">
      <Flex>
        {size !== 'sm' && (
          <Avatar src={currContact?.avatar} name={currContact?.first_name} />
        )}
        <Box flex="1" ml={size !== 'sm' ? '20px' : 0}>
          <Formik
            validateOnBlur={false}
            initialValues={{
              body: note?.body || '',
              private: note?.private || false,
            }}
            validationSchema={noteSchema}
            onSubmit={onSubmit}
          >
            {(props) => (
              <Form noValidate>
                <Field name="body">
                  {({ field, form }) => (
                    <FormControl
                      mb="12px"
                      isRequired
                      isInvalid={form.errors.body && form.touched.body}
                      background="secondary-50"
                    >
                      <Textarea
                        {...field}
                        id="body"
                        name="body"
                        placeholder="start typing.."
                        border="1px solid"
                        borderColor="border-color"
                        _hover={{
                          border: '1px solid',
                          borderColor: 'border-color',
                        }}
                        _focus={{
                          border: '1px solid',
                          borderColor: 'border-color',
                        }}
                      />
                      <FormErrorMessage>{form.errors.body}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>

                <Flex mt="xl" alignItems="center">
                  <Box flex="1" cursor="pointer">
                    <FormLabel>
                      <Field type="checkbox" name="private" />
                      <KText
                        color="secondary-500"
                        as="span"
                        variant="muted"
                        ml="8px"
                      >
                        Private Note
                      </KText>
                    </FormLabel>
                  </Box>
                  <Box>
                    {edit && (
                      <Button variant="secondary" mr="lg" onClick={onCancel}>
                        Cancel
                      </Button>
                    )}
                    <Button type="submit" isLoading={props.isSubmitting}>
                      {edit ? 'Update Note' : 'Add Note'}
                    </Button>
                  </Box>
                </Flex>
              </Form>
            )}
          </Formik>
        </Box>
      </Flex>
    </Box>
  );
};

export const Note = ({ note, contact, size = 'lg' }) => {
  const [edit, setEdit] = useState(false);
  const toast = useShowToast();
  const dispatch = useDispatch();
  const onEdit = () => {
    setEdit(true);
  };

  const onUpdateNote = async ({ body, private: isPrivate }) => {
    await dispatch(
      updateCandidateNotes({
        body,
        isPrivate,
        noteId: note?.id,
        contactId: contact?.id,
      })
    );
    toast({
      title: 'Note updated',
      status: 'success',
      position: 'bottom-left',
      duration: 9000,
    });
    setEdit(false);
  };

  if (edit) {
    return (
      <NoteForm
        edit={edit}
        note={note}
        onCancel={() => setEdit(false)}
        onSave={onUpdateNote}
        size={size}
      />
    );
  }

  return <NoteView note={note} onEdit={onEdit} size={size} />;
};
